/**
 * 🔴 21/10/2026 — "PRECISA DE CONFERÊNCIA": o que o espelho e a leitura de cadastros do Omie não gravaram sozinhos (mig 741).
 *
 * O caso: em 20/10 um cadastro do Omie que era o Tamboré foi editado lá e virou a Clarice Paisagismo, e o sistema copiou tudo. Agora
 * o Omie dizer que um cadastro "virou outra empresa" (CNPJ, ou razão social ou nome fantasia que muda totalmente), ou achar o cliente
 * em outro código do Omie, cai aqui com o que está aqui e o que o Omie diz. Um administrador decide: aplicar o do Omie ou manter o daqui.
 * Uma linha fechada (regra "aviso é uma linha"); o detalhe e os botões só aparecem ao abrir.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Aviso, Button } from '@/components/ui';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format-date';

interface CampoDaPendencia {
  rotulo: string;
  daqui: string | null;
  doOmie: string | null;
}
interface DetalheVinculo {
  empresaId: string;
  codigoAtual: string;
  codigoNovo: string;
  nomeNovo: string | null;
  documentoNovo: string | null;
}
interface Pendencia {
  id: string;
  clienteId: string;
  clienteNome: string;
  tipo: 'identidade' | 'vinculo';
  empresaId: string | null;
  detalhe: CampoDaPendencia[] | DetalheVinculo;
  detectadoEmIso: string;
}

const NOME_DA_EMPRESA: Record<string, string> = {
  'empresa-igm-ag': 'AG DOS SANTOS',
  'empresa-igm-mussi': 'MATHEUS MUSSI',
};

function OQueOOmieDiz({ p }: { p: Pendencia }) {
  if (p.tipo === 'vinculo') {
    const d = p.detalhe as DetalheVinculo;
    return (
      <p className="text-sm">
        Na empresa {NOME_DA_EMPRESA[d.empresaId] ?? d.empresaId}, a leitura do Omie achou este cliente no código {d.codigoNovo}
        {d.nomeNovo ? ` (${d.nomeNovo})` : ''}, e ele está ligado ao código {d.codigoAtual}. O vínculo não foi trocado.
      </p>
    );
  }
  return (
    <ul className="space-y-1 text-sm" data-testid="conferencia-omie-campos">
      {(p.detalhe as CampoDaPendencia[]).map((c, i) => (
        <li key={i}>
          <span className="font-medium">{c.rotulo}:</span> aqui “{c.daqui ?? 'vazio'}”, no Omie “{c.doOmie ?? 'vazio'}”
        </li>
      ))}
    </ul>
  );
}

export function PrecisaDeConferenciaOmie({ ehAdmin }: { ehAdmin: boolean }) {
  const [pendencias, setPendencias] = useState<Pendencia[] | null>(null);
  const [decidindo, setDecidindo] = useState<string | null>(null);
  const [erro, setErro] = useState<{ id: string; texto: string } | null>(null);

  useEffect(() => {
    if (!ehAdmin) return;
    let vivo = true;
    Promise.resolve()
      .then(() => api.get<{ pendencias: Pendencia[] }>('/api/v1/clientes/conferencia-omie/pendencias'))
      .then((r) => {
        if (vivo && r) setPendencias(r.pendencias);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [ehAdmin]);

  if (!ehAdmin || !pendencias || pendencias.length === 0) return null;

  async function decidir(p: Pendencia, decisao: 'aplicar' | 'manter') {
    setDecidindo(`${p.id}:${decisao}`);
    setErro(null);
    try {
      const r = await api.post<{ pendencias: Pendencia[] }>(`/api/v1/clientes/conferencia-omie/pendencias/${p.id}/${decisao}`, {});
      setPendencias(r.pendencias);
    } catch (e) {
      setErro({ id: p.id, texto: (e as { mensagem?: string })?.mensagem ?? 'Não foi possível gravar a decisão.' });
    } finally {
      setDecidindo(null);
    }
  }

  const n = pendencias.length;
  return (
    <Aviso
      tom="perigo"
      className="mb-4"
      data-testid="clientes-precisa-de-conferencia"
      fato={n === 1 ? '1 cadastro precisa de conferência com o Omie' : `${n} cadastros precisam de conferência com o Omie`}
    >
      <ul className="space-y-4" data-testid="conferencia-omie-lista">
        {pendencias.map((p) => (
          <li key={p.id} className="space-y-2" data-testid="conferencia-omie-item">
            <p className="text-sm">
              <Link to={`/admin/clientes-v2/${p.clienteId}`} className="font-semibold underline">
                {p.clienteNome}
              </Link>{' '}
              <span className="text-ink-3 dark:text-ink-dark-3">· desde {formatDateTime(p.detectadoEmIso)}</span>
            </p>
            {p.tipo === 'identidade' && (
              <p className="text-sm">O Omie diz que este cadastro virou outra empresa. Nada foi gravado.</p>
            )}
            <OQueOOmieDiz p={p} />
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                loading={decidindo === `${p.id}:manter`}
                disabled={decidindo !== null}
                onClick={() => decidir(p, 'manter')}
                data-testid="conferencia-omie-manter"
              >
                Manter o daqui
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={decidindo === `${p.id}:aplicar`}
                disabled={decidindo !== null}
                onClick={() => decidir(p, 'aplicar')}
                data-testid="conferencia-omie-aplicar"
              >
                Aplicar o do Omie
              </Button>
            </div>
            {erro?.id === p.id && (
              <p role="alert" className="text-sm text-red-deep dark:text-red-glow" data-testid="conferencia-omie-erro">
                {erro.texto}
              </p>
            )}
          </li>
        ))}
      </ul>
    </Aviso>
  );
}
